import { DateTime } from 'luxon';
import {
  isValid as dateFns_isValid,
  isDate as dateFns_isDate,
  parseISO,
} from 'date-fns';

import { usePreferences } from './usePreferences';

interface IDateHelpers {
  convertTZ: (date: Date) => Date;
  currentTimeZone: string;
  localizeDate: (value: Date | string) => DateTime;
  toFormattedDateString: (value?: Date | string) => string;
  toFormattedFullDateString: (value?: Date | string) => string;
  toFormattedDateTimeString: (value?: Date | string) => string;
  toFormattedFullDateTimeString: (value?: Date | string) => string;
  toFormattedTimeString: (value?: Date | string) => string;
}

export function useDate(): IDateHelpers {
  const { timeZone, dateTimeFormat, dateFormat } = usePreferences();

  function localizeDate(value: Date | string): DateTime {
    if (typeof value === 'string') {
      return DateTime.fromISO(value, { zone: timeZone });
    }

    if (isDate(value)) {
      return DateTime.fromJSDate(value, { zone: timeZone });
    }

    return DateTime.local({ zone: timeZone });
  }

  function toFormattedDateString(value?: Date | string): string {
    if (!value) {
      return '';
    }

    const dt = localizeDate(value);
    if (!dt.isValid) {
      return '';
    }

    // Using existing `formatDate` logic.
    // Ideally this will be updated to use a library method.
    let dateString = dateTimeFormat.split(" ")[0];
    dateString = dateString.replace("%D", dt.day.toString().padStart(2, "0"));
    dateString = dateString.replace("%d", dt.day.toString().padStart(2, "0"));
    dateString = dateString.replace("%m", dt.month.toString().padStart(2, "0"));
    dateString = dateString.replace("%M", dt.month.toString().padStart(2, "0"));
    dateString = dateString.replace("%Y", dt.year.toString());
    dateString = dateString.replace("%y", (dt.year - 2000).toString().padStart(2, "0"));
    return dateString.split(" ")[0];
  }

  function toFormattedTimeString(value?: Date | string): string {
    if (!value) {
      return '';
    }

    const dt = localizeDate(value);
    if (!dt.isValid) {
      return '';
    }

    // Using existing `formatTime` logic.
    // Ideally this will be updated to use a library method.
    let timeString = dateTimeFormat.split(" ")[1];
    timeString = timeString.replace("%H", dt.hour.toString().padStart(2, "0"));
    timeString = timeString.replace("%h", dt.hour.toString().padStart(2, "0"));
    timeString = timeString.replace("%i", dt.minute.toString().padStart(2, "0"));
    timeString = timeString.replace("%I", dt.minute.toString().padStart(2, "0"));
    timeString = timeString.replace("%s", dt.second.toString().padStart(2, "0"));
    timeString = timeString.replace("%S", dt.second.toString().padStart(2, "0"));
    return timeString;
  }

  function toFormattedFullDateString(value?: Date | string): string {

    if (!value) {
      return '';
    }

    const date = new Date(value);

    return formatDateString(DateTime.fromJSDate(date), dateFormat.slice());
  }

  function toFormattedDateTimeString(value?: Date | string): string {
    if (!value) {
      return '';
    }

    // Using existing `formatDateTime` logic.
    // Ideally this will be updated to use a library method.
    const dateString = toFormattedDateString(value);
    if (!dateString || !dateString.length) {
      return '';
    }

    const timeString = toFormattedTimeString(value);

    return `${dateString} ${timeString}`;
  }

  function toFormattedFullDateTimeString(value?: Date | string): string {

    if (!value) {
      return '';
    }

    // Using existing `formatDateTime` logic.
    // Ideally this will be updated to use a library method.
    const dateString = toFormattedFullDateString(value);
    if (!dateString || !dateString.length) {
      return '';
    }

    const timeString = toFormattedTimeString(value);

    return `${dateString} ${timeString}`;
  }

  // WARNING: this function returns incorrect date values for the sake of a component that does not respect timezones.
  // Use at your own risk.
  function convertTZ(date: Date) {
    const { localOffsetHours, adjustedOffsetHours } = getTimeZoneOffsets(timeZone);

    date.setHours(date.getHours() - localOffsetHours + adjustedOffsetHours);

    return date;
  }

  return {
    convertTZ,
    currentTimeZone: timeZone,
    localizeDate,
    toFormattedDateString,
    toFormattedFullDateString,
    toFormattedDateTimeString,
    toFormattedFullDateTimeString,
    toFormattedTimeString,
  }
}

export function isDate(value: unknown): value is Date {
  return dateFns_isDate(value);
}

export function isValidDate(value: unknown): boolean {
  if (DateTime.isDateTime(value)) {
    return value.isValid
  }

  if (typeof value === 'string') {
    const parsedISO = parseISO(value);

    return dateFns_isValid(parsedISO);
  }

  return dateFns_isValid(value);
}

function formatDateString(date: DateTime, dateFormat: string) {
  let dateString = dateFormat;

  dateString = dateString.replace("%D", date.day.toString().padStart(2, "0"));
  dateString = dateString.replace("%d", date.day.toString().padStart(2, "0"));
  dateString = dateString.replace("%m", date.month.toString().padStart(2, "0"));
  dateString = dateString.replace("%M", date.month.toString().padStart(2, "0"));
  dateString = dateString.replace("%Y", date.year.toString());
  dateString = dateString.replace("%y", (date.year - 2000).toString().padStart(2, "0"));

  return dateString;
}

function getTimeZoneOffsets(timeZone: string) {

  const date = new Date();

  // Abuse the Intl API to get a local and adjusted ISO string for a given time zone.
  const localIso = date.toLocaleString('en-CA', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, hour12: false }).replace(', ', 'T');
  const adjustedIso = date.toLocaleString('en-CA', { timeZone, hour12: false }).replace(', ', 'T');

  // Lie to the Date object constructor that it's a UTC time...
  const fakeLocalUTC = new Date(localIso + 'Z');
  const fakeAdjustedUTC = new Date(adjustedIso + 'Z');

  // Return the difference in timestamps, as hours
  // Negative values are west of GMT, positive east
  const localOffsetHours = parseInt(((fakeLocalUTC.getTime() - date.getTime()) / 1000 / 60 / 60).toString());
  const adjustedOffsetHours = parseInt(((fakeAdjustedUTC.getTime() - date.getTime()) / 1000 / 60 / 60).toString());

  return { localOffsetHours, adjustedOffsetHours };
}